import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../authentication/AuthContext";
import { useDueRecallCards, useReviewRecallCard } from "./hooks/useRecall";
import Header from "../components/Header";
import { ArrowLeft, CheckCircle2, XCircle, Clock3, Lightbulb } from "lucide-react";
import CodeBlock from "./components/CodeBlock";
import { getNextLearningStep } from "./recallScheduling";

export const RecallReviewSession = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const activeFolder = searchParams.get("folder");
    const activeCardIds = useMemo(
        () => (searchParams.get("cards") ?? "")
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean),
        [searchParams]
    );
    const { authToken } = useAuth();
    const { data: dueCards, isFetched, isFetching } = useDueRecallCards(authToken);
    const { mutateAsync: submitReview, isLoading: isReviewing } = useReviewRecallCard(authToken);

    const dueCardsInScope = useMemo(() => {
        if (!dueCards) return [];
        if (activeCardIds.length > 0) {
            return dueCards.filter((c) => activeCardIds.includes(String(c.id)));
        }
        return activeFolder
            ? dueCards.filter(c => c.parentFolderId === activeFolder)
            : dueCards;
    }, [dueCards, activeCardIds, activeFolder]);

    // Keep the active session independent from due-card query refetches. A review
    // moves the card's due date into the future, but that must not remove it from
    // the session before we decide whether it needs another attempt.
    const [cards, setCards] = useState(null);
    const [index, setIndex] = useState(0);
    const [flipped, setFlipped] = useState(false);
    const [hintRevealed, setHintRevealed] = useState(false);
    const [results, setResults] = useState([]);
    const [scheduledCards, setScheduledCards] = useState([]);
    const [clockNow, setClockNow] = useState(Date.now());
    const [done, setDone] = useState(false);
    const [reviewError, setReviewError] = useState("");
    const flippedAtRef = useRef(null);

    useEffect(() => {
        if (!isFetched || isFetching || cards !== null) return;
        setCards(dueCardsInScope);
    }, [cards, dueCardsInScope, isFetched, isFetching]);

    const sessionCards = cards ?? [];
    const current = sessionCards[index];
    const progress = sessionCards.length > 0 ? (index / sessionCards.length) * 100 : 0;
    const isWaitingForLearningStep = !current && scheduledCards.length > 0;

    useEffect(() => {
        flippedAtRef.current = flipped ? Date.now() : null;
    }, [flipped, current?.id]);

    useEffect(() => {
        setHintRevealed(false);
    }, [current?.id]);

    useEffect(() => {
        if (!isWaitingForLearningStep) return undefined;

        const releaseDueCards = () => {
            const now = Date.now();
            const dueCards = scheduledCards.filter(({ dueAt }) => dueAt <= now);
            setClockNow(now);

            if (dueCards.length === 0) return;

            setCards(existing => [...existing, ...dueCards.map(({ card }) => card)]);
            setScheduledCards(existing => existing.filter(({ dueAt }) => dueAt > now));
            setFlipped(false);
        };

        releaseDueCards();
        const timer = window.setInterval(releaseDueCards, 1000);
        return () => window.clearInterval(timer);
    }, [isWaitingForLearningStep, scheduledCards]);

    const handleRate = async (rating) => {
        if (!current || isReviewing) return;

        setReviewError("");

        try {
            const flippedAt = flippedAtRef.current ?? Date.now();

            const updatedCard = await submitReview({
                recallCardId: current.id,
                reviewData: {
                    rating: rating.toUpperCase(),
                    recall_time_ms: Math.max(0, Date.now() - flippedAt),
                },
            });

            setResults(prev => [...prev, { card: current, rating }]);

            const learningStep = getNextLearningStep(updatedCard);
            const withoutCurrentCard = scheduledCards.filter(
                ({ card }) => String(card.id) !== String(updatedCard.id)
            );
            const nextScheduledCards = learningStep
                ? [...withoutCurrentCard, learningStep]
                : withoutCurrentCard;
            const now = Date.now();
            const readyLearningCards = nextScheduledCards.filter(({ dueAt }) => dueAt <= now);
            const waitingLearningCards = nextScheduledCards.filter(({ dueAt }) => dueAt > now);
            const nextCards = [...sessionCards, ...readyLearningCards.map(({ card }) => card)];

            setCards(nextCards);
            setScheduledCards(waitingLearningCards);

            const next = index + 1;
            if (next >= nextCards.length && waitingLearningCards.length === 0) {
                setDone(true);
            } else {
                setIndex(next);
                setFlipped(false);
            }
        } catch (error) {
            setReviewError(error instanceof Error ? error.message : "Failed to review recall card");
        }
    };

    if (cards === null) {
        return (
            <div className="min-h-screen bg-[#12131a] flex items-center justify-center">
                <p className="text-gray-400 text-sm animate-pulse">Loading cards...</p>
            </div>
        );
    }

    if (!sessionCards.length) {
        return (
            <div className="min-h-screen bg-[#12131a] flex flex-col items-center justify-center gap-4 px-6 text-center">
                <div className="text-5xl mb-2">🎉</div>
                <h2 className="text-xl font-bold text-white">All caught up!</h2>
                <p className="text-gray-400 text-sm max-w-xs">No cards are due for review today. Come back tomorrow to keep your streak going.</p>
                <button onClick={() => navigate("/recall")} className="mt-4 text-indigo-400 hover:underline text-sm">← Back to Cards</button>
            </div>
        );
    }

    if (isWaitingForLearningStep) {
        const nextDueAt = Math.min(...scheduledCards.map(({ dueAt }) => dueAt));
        const secondsUntilNextCard = Math.max(0, Math.ceil((nextDueAt - clockNow) / 1000));
        const minutes = Math.floor(secondsUntilNextCard / 60);
        const seconds = secondsUntilNextCard % 60;

        return (
            <div className="min-h-screen bg-[#12131a]">
                <Header />
                <div className="mx-auto flex max-w-lg flex-col items-center px-6 pb-16 pt-32 text-center">
                    <Clock3 className="mb-5 text-indigo-400" size={42} />
                    <h1 className="mb-2 text-2xl font-bold text-white">Next learning step</h1>
                    <p className="mb-8 text-sm text-gray-400">
                        The backend scheduled the next card for this session.
                    </p>
                    <p className="mb-8 font-mono text-4xl font-semibold tabular-nums text-white">
                        {minutes}:{String(seconds).padStart(2, "0")}
                    </p>
                    <button
                        onClick={() => navigate("/recall")}
                        className="rounded-xl border border-gray-700 px-5 py-3 text-sm font-medium text-gray-300 transition hover:bg-gray-800"
                    >
                        Finish for now
                    </button>
                </div>
            </div>
        );
    }

    /* ── Summary screen ── */
    if (done) {
        const correct = results.filter(r => r.rating === "good" || r.rating === "easy").length;
        const wrong = results.filter(r => r.rating === "again" || r.rating === "hard").length;
        const pct = Math.round((correct / results.length) * 100);

        return (
            <div className="min-h-screen bg-[#12131a]">
                <Header />
                <div className="max-w-lg mx-auto px-6 pt-28 pb-16 flex flex-col items-center text-center">
                    <div className="text-5xl mb-4">{pct >= 70 ? "🎉" : "💪"}</div>
                    <h1 className="text-2xl font-bold text-white mb-1">Session Complete</h1>
                    <p className="text-gray-400 text-sm mb-10">{results.length} reviews completed</p>

                    <div className="w-full grid grid-cols-2 gap-4 mb-10">
                        <div className="bg-[#1E1F25] border border-green-500/20 rounded-2xl p-5">
                            <CheckCircle2 className="text-green-400 mx-auto mb-2" size={24} />
                            <p className="text-3xl font-bold text-white">{correct}</p>
                            <p className="text-xs text-gray-400 mt-1">Correct</p>
                        </div>
                        <div className="bg-[#1E1F25] border border-red-500/20 rounded-2xl p-5">
                            <XCircle className="text-red-400 mx-auto mb-2" size={24} />
                            <p className="text-3xl font-bold text-white">{wrong}</p>
                            <p className="text-xs text-gray-400 mt-1">Wrong</p>
                        </div>
                    </div>

                    {/* Score bar */}
                    <div className="w-full bg-gray-800 rounded-full h-2 mb-10 overflow-hidden">
                        <div
                            className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-700"
                            style={{ width: `${pct}%` }}
                        />
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={() => navigate("/recall")}
                            className="px-5 py-3 rounded-xl border border-gray-700 text-gray-300 hover:bg-gray-800 text-sm font-medium transition"
                        >
                            Back to Cards
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    /* ── Review screen ── */
    return (
        <div className="min-h-screen bg-[#12131a]">
            <Header />
            <div className="max-w-xl mx-auto px-6 pt-24 pb-16">

                {/* Back + progress */}
                <div className="flex items-center gap-4 mb-6">
                    <button
                        onClick={() => navigate("/recall")}
                        className="text-gray-500 hover:text-gray-300 transition"
                    >
                        <ArrowLeft size={18} />
                    </button>
                    <div className="flex-1 bg-gray-800 rounded-full h-1.5 overflow-hidden">
                        <div
                            className="h-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-500"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <span className="text-gray-500 text-xs tabular-nums">{index + 1} / {sessionCards.length}</span>
                </div>

                {/* Card */}
                <button
                    type="button"
                    className="relative w-full cursor-pointer select-none text-left"
                    style={{ perspective: "1000px" }}
                    onClick={() => !isReviewing && setFlipped(f => !f)}
                >
                    <div
                        className="relative w-full transition-transform duration-500"
                        style={{
                            transformStyle: "preserve-3d",
                            transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
                            minHeight: "260px",
                        }}
                    >
                        {/* Front */}
                        <div
                            className="absolute inset-0 bg-[#1E1F25] border border-[#2A2B32] rounded-2xl p-8 flex flex-col justify-between shadow-xl"
                            style={{ backfaceVisibility: "hidden" }}
                        >
                            <span className="text-xs text-gray-600 uppercase tracking-widest">Front</span>
                            <div className="flex-1 flex items-center justify-center px-2 py-2 overflow-auto">
                                <CodeBlock
                                    text={current.front}
                                    lang={current.lang ?? "javascript"}
                                    className="text-white text-xl font-semibold leading-relaxed text-center"
                                />
                            </div>
                            <p className="text-xs text-gray-600 text-center">Tap to reveal answer</p>
                        </div>

                        {/* Back */}
                        <div
                            className="absolute inset-0 bg-[#1E1F25] border border-indigo-500/30 rounded-2xl p-8 flex flex-col justify-between shadow-xl"
                            style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
                        >
                            <span className="text-xs text-indigo-400 uppercase tracking-widest">Back</span>
                            <div className="flex-1 flex items-center justify-center px-2 py-2 overflow-auto">
                                <CodeBlock
                                    text={current.back}
                                    lang={current.lang ?? "javascript"}
                                    className="text-gray-200 text-lg leading-relaxed text-center"
                                />
                            </div>
                            <p className="text-xs text-gray-600 text-center">Tap to see the front</p>
                        </div>
                    </div>
                </button>

                {/* Hint — available separately while viewing the front */}
                {!flipped && current.hint && (
                    <div className="mt-4 flex flex-col items-center gap-3">
                        <button
                            type="button"
                            onClick={() => setHintRevealed(revealed => !revealed)}
                            className="inline-flex items-center gap-2 rounded-xl border border-amber-500/30 px-4 py-2 text-sm font-medium text-amber-400 transition hover:bg-amber-500/10"
                        >
                            <Lightbulb size={15} />
                            {hintRevealed ? "Hide Hint" : "Reveal Hint"}
                        </button>
                        {hintRevealed && (
                            <p className="w-full whitespace-pre-wrap rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-center text-sm text-amber-100/80">
                                {current.hint}
                            </p>
                        )}
                    </div>
                )}

                {/* Rating buttons — only shown after flip */}
                <div className={`mt-6 grid grid-cols-4 gap-3 transition-all duration-300 ${flipped ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"}`}>
                    {[
                        { label: "Again", rating: "again", color: "border-red-500/40 text-red-400 hover:bg-red-500/10" },
                        { label: "Hard",  rating: "hard",  color: "border-orange-500/40 text-orange-400 hover:bg-orange-500/10" },
                        { label: "Good",  rating: "good",  color: "border-green-500/40 text-green-400 hover:bg-green-500/10" },
                        { label: "Easy",  rating: "easy",  color: "border-blue-500/40 text-blue-400 hover:bg-blue-500/10" },
                    ].map(({ label, rating, color }) => (
                        <button
                            key={rating}
                            onClick={(e) => { e.stopPropagation(); handleRate(rating); }}
                            disabled={isReviewing}
                            className={`py-3 rounded-xl border text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60 ${color}`}
                        >
                            {isReviewing ? "Saving..." : label}
                        </button>
                    ))}
                </div>

                {reviewError && (
                    <p className="text-center text-red-400 text-xs mt-4">{reviewError}</p>
                )}

                {!flipped && (
                    <p className="text-center text-gray-600 text-xs mt-6">Click the card to see the answer</p>
                )}
            </div>
        </div>
    );
};
